import { Fetcher, LOCAL_STORAGE_AUTHENTICATED_USER } from "@/services/Fetcher";
import useObject from "@/composable/components/object";
import { CreateUserRequest, CreateUserResult, LoginAdminResult, OreSiUserStates, Serializable } from "@/types/generated/UsedGeneratedTypes";

interface UserIdResult {
  id: string;
}

// ─── Interfaces de dépendances injectables ─────────────────────────────────────

export interface LoginServiceRouter {
  push: (path: string) => Promise<void> | void;
  currentRoute: { value: { path: string } };
}

export interface LoginServiceDependencies {
  router?: LoginServiceRouter | null;
}

export class LoginService extends Fetcher {
  static INSTANCE: LoginService = new LoginService();
  static #dependencies: LoginServiceDependencies = {};
  #useUser = useObject<LoginAdminResult>();

  constructor() {
    super();
  }

  static configureService(deps: LoginServiceDependencies): void {
    LoginService.#dependencies = { ...LoginService.#dependencies, ...deps };
  }

  static resetDependencies(): void {
    LoginService.#dependencies = {};
  }

  protected get _router(): LoginServiceRouter | null {
    return LoginService.#dependencies.router ?? null;
  }

  getAuthenticatedUser(applicationName?: string): LoginAdminResult {
    const useUser = this.#useUser;
    const userRaw = localStorage.getItem(LOCAL_STORAGE_AUTHENTICATED_USER);
    const userData = userRaw ? JSON.parse(userRaw) : {};
    const stateKey = userData.state as OreSiUserStates;
    if (this._router?.currentRoute.value.path !== "/login" && stateKey !== "active") {
      this.logout(applicationName);
    }
    if (useUser.reactiveObject.login === "") {
      if (this._router?.currentRoute.value.path !== "/login") {
        this.logout(applicationName);
      }
    }
    if (!useUser.reactiveObject.login) {
      if (!userData) {
        if (this._router?.currentRoute.value.path !== "/login") {
          this.logout(applicationName);
        }
      }
      else {
        this.setAuthenticateduser(userData);
      }
    }
    return useUser.reactiveObject;
  }

  updateAuthenticateduser(user: CreateUserResult): LoginAdminResult {
    const updatedUser = this.getAuthenticatedUser();
    if (updatedUser == null) {
      return this.setAuthenticateduser();
    }
    updatedUser.chartes = user.chartes;
    updatedUser.email = user.email;

    // Stocker l'utilisateur dans le sessionStorage
    localStorage.setItem(LOCAL_STORAGE_AUTHENTICATED_USER, JSON.stringify(updatedUser));
    return updatedUser;
  }

  setAuthenticateduser(user: LoginAdminResult = this.getAuthenticatedUser()): LoginAdminResult {
    const newUser = {
      ...user,
    };

    localStorage.setItem(LOCAL_STORAGE_AUTHENTICATED_USER, JSON.stringify(newUser));
    this.#useUser.doChangeObject(newUser);
    return newUser;
  }

  async signIn(login: string, pwd: string, applicationName?: string, returnUrl?: string | null): Promise<LoginAdminResult> {
    const response = await this.post<LoginAdminResult>("login", {
      login,
      password: pwd,
    });
    this.setAuthenticateduser(response);
    // À chaque nouvelle connexion , on remet à zéro les drapeaux UX
    // session-scopés ( bandeaux fermés , dismissals one-shot ) pour
    // que l'utilisateur retrouve l'UI dans son état nominal. Une
    // déconnexion + reconnexion dans le même onglet relance ainsi le
    // bandeau d'info "limite 500 lignes".
    sessionStorage.removeItem("OA_DISMISS_INFO_500");
    // #470 - Si la navigation vers /login provient d'une route protégée
    // ( garde router.beforeEach ) , on retourne à cette route après un
    // login réussi. Priorité sur applicationName qui reste le
    // comportement par défaut.
    if (returnUrl) {
      this._router?.push(returnUrl);
    }
    else if (applicationName) {
      this._router?.push("/applications/" + applicationName);
    }
    else {
      this._router?.push("/applications");
    }
    return response;
  }

  async register(login: string, pwd: string, email: string): Promise<UserIdResult> {
    return await this.post<UserIdResult>("users", {
      login,
      password: pwd,
      email,
    });
  }

  async modifAcount(params: CreateUserRequest): Promise<CreateUserResult> {
    const response = await this.put<CreateUserResult>("users", params, false);
    this.updateAuthenticateduser(response);
    return response;
  }

  async logout(applicationName?: string): Promise<void> {
    try {
      await this.delete("logout");
    }
    catch {
      // #470 - Si la session est déjà morte côté serveur, on poursuit
      // le nettoyage local pour ne pas bloquer la redirection.
    }
    // #470 - Suppression ciblée : on ne touche qu'aux clés liées à
    // l'authentification ( profil utilisateur + bearer token ) pour
    // préserver les préférences utilisateur ( langue , etc. ).
    localStorage.removeItem(LOCAL_STORAGE_AUTHENTICATED_USER);
    this.setAuthorizationToken(null);
    this.notifyCredentialsLost();
    // Marqueur de "déconnexion explicite" lu par le guard router avant
    // d'afficher le toast d'arrivée sur /login. Sans ce flag , le guard
    // affichait systématiquement "Votre session a expiré" même quand
    // l'utilisateur avait cliqué volontairement sur "Se déconnecter".
    // sessionStorage : ne survit pas à un rechargement de l'onglet ,
    // ne pollue pas les autres onglets - parfait pour un signal one-shot.
    sessionStorage.setItem("OA_INTENTIONAL_LOGOUT", "1");
    if (applicationName) {
      this._router?.push("/login/" + applicationName);
    }
    else {
      this._router?.push("/login");
    }
  }

  async refreshSession(): Promise<Serializable> {
    // #470 - POST /refresh retourne un nouveau JWT dans l'en-tête
    // Authorization de la réponse. Le Fetcher le capture automatiquement
    // via _handleResponse et remplace le token courant en sessionStorage.
    // Le timer d'inactivité est réinitialisé en amont par le SessionService
    // sur la base d'une réponse 2xx.
    return await this.post("refresh", {});
  }

  // #470 - Récupère la configuration de session exposée par le backend
  // ( TTL du JWT en secondes ) , pour que le SessionService aligne son
  // timer d'inactivité sur le serveur. Endpoint anonyme , appelable
  // avant authentification.
  async getSessionConfig(): Promise<{
    jwtExpirationSeconds: number;
    // Audit OA_FULL_REVIEW (8/5/26) - mode actif backend pour le compteur
    // de lignes par référencetype ( cf. SessionService + ApplicationInfoView ).
    referencevalueCountUseStatsTable?: boolean;
    // Bornes du cache ETag JS-side , appliquées par SessionService au boot
    // ( cf. EtagCache.resize / DataService.configureFilterListEtagCache ).
    frontEtagCacheMaxEntries?: number;
    frontEtagCacheMaxBytesMb?: number;
  }> {
    return await this.get("session/config");
  }

  invalidateCharte(applicationId: string, loginAdminResult: LoginAdminResult) {
    if (!loginAdminResult.chartes[applicationId]) {
    // @ts-expect-error refactoring in progress TODO Type adress this issue
      loginAdminResult.chartes[applicationId] = false;
    }
  }

  isApplicationCreator(applicationName: string, loginAdminResult: LoginAdminResult) {
    return loginAdminResult.currentUserRoles?.isApplicationCreator || this.hasAuthorization(applicationName, loginAdminResult);
  }

  hasAuthorization(pattern: string, loginAdminResult: LoginAdminResult) {
    return loginAdminResult.authorizations.some(auth => new RegExp(auth).test(pattern));
  }

  getStoredAuthenticatedUser(): LoginAdminResult | undefined {
    const authenticatedUser = localStorage.getItem(LOCAL_STORAGE_AUTHENTICATED_USER);
    return authenticatedUser ? JSON.parse(authenticatedUser) : undefined;
  }
}
